/**
 * Storage Quota Service
 * Manages user storage limits and provides fallback to local device storage
 * Standard: 50GB, Pro/Business: 250GB per user
 */

import { supabase } from '../lib/supabaseClient';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

export interface StorageStatus {
  quota_bytes: number;
  used_bytes: number;
  available_bytes: number;
  percent_used: number;
  quota_gb: number;
  used_gb: number;
  available_gb: number;
}

export interface QuotaCheckResult {
  allowed: boolean;
  quota_bytes: number;
  used_bytes: number;
  available_bytes: number;
  file_size: number;
  would_exceed_by?: number;
  new_usage?: number;
}

class StorageQuotaService {
  private static instance: StorageQuotaService;
  private cachedStatus: StorageStatus | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 30 * 1000; // 30 seconds

  private constructor() {}

  static getInstance(): StorageQuotaService {
    if (!StorageQuotaService.instance) {
      StorageQuotaService.instance = new StorageQuotaService();
    }
    return StorageQuotaService.instance;
  }

  /**
   * Get current user's storage status
   */
  async getStorageStatus(forceRefresh = false): Promise<StorageStatus | null> {
    // Return cached if valid and not forcing refresh
    if (!forceRefresh && this.cachedStatus && Date.now() - this.cacheTimestamp < this.CACHE_DURATION) {
      return this.cachedStatus;
    }

    try {
      const { data, error } = await supabase.rpc('get_my_storage_status');

      if (error) {
        console.error('[StorageQuota] Error fetching storage status:', error);
        return null;
      }

      this.cachedStatus = data as StorageStatus;
      this.cacheTimestamp = Date.now();
      return data as StorageStatus;
    } catch (error) {
      console.error('[StorageQuota] Error:', error);
      return null;
    }
  }

  /**
   * Check if upload would exceed quota (before uploading)
   */
  async checkQuotaBeforeUpload(fileSize: number): Promise<QuotaCheckResult | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase.rpc('check_storage_quota_before_upload', {
        p_user_id: user.id,
        p_file_size: fileSize
      });

      if (error) {
        console.error('[StorageQuota] Error checking quota:', error);
        return null;
      }

      return data as QuotaCheckResult;
    } catch (error) {
      console.error('[StorageQuota] Error:', error);
      return null;
    }
  }

  /**
   * Update storage usage after successful upload
   */
  async updateUsageAfterUpload(fileSize: number): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase.rpc('update_storage_usage_after_upload', {
        p_user_id: user.id,
        p_file_size: fileSize
      });

      if (error) {
        console.error('[StorageQuota] Error updating usage:', error);
        return false;
      }

      // Clear cache to force refresh on next getStorageStatus
      this.clearCache();
      return true;
    } catch (error) {
      console.error('[StorageQuota] Error:', error);
      return false;
    }
  }

  /**
   * Update storage usage after file deletion
   */
  async updateUsageAfterDelete(fileSize: number): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase.rpc('update_storage_usage_after_delete', {
        p_user_id: user.id,
        p_file_size: fileSize
      });

      if (error) {
        console.error('[StorageQuota] Error updating usage:', error);
        return false;
      }

      // Clear cache
      this.clearCache();
      return true;
    } catch (error) {
      console.error('[StorageQuota] Error:', error);
      return false;
    }
  }

  /**
   * Save file to local device storage (fallback when cloud quota exceeded)
   */
  async saveToLocalDevice(fileName: string, data: string | Blob, mimeType: string = 'application/octet-stream'): Promise<{ success: boolean; path?: string; error?: string }> {
    try {
      // Only works on native platforms
      if (!Capacitor.isNativePlatform()) {
        // For web, trigger download
        return this.downloadInBrowser(fileName, data, mimeType);
      }

      let base64Data: string;

      // Convert Blob to base64 if needed
      if (data instanceof Blob) {
        base64Data = await this.blobToBase64(data);
      } else {
        base64Data = data;
      }

      // Save to device's Documents directory
      const result = await Filesystem.writeFile({
        path: `NoorCAD/${fileName}`,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true
      });

      console.log('[StorageQuota] File saved to local device:', result.uri);
      
      return {
        success: true,
        path: result.uri
      };
    } catch (error) {
      console.error('[StorageQuota] Error saving to device:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to save to device'
      };
    }
  }

  /**
   * Download file in browser (web fallback)
   */
  private downloadInBrowser(fileName: string, data: string | Blob, mimeType: string): { success: boolean; path?: string } {
    try {
      let blob: Blob;

      if (data instanceof Blob) {
        blob = data;
      } else {
        // Assume base64 string
        const byteCharacters = atob(data.includes('base64,') ? data.split('base64,')[1] : data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: mimeType });
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return { success: true, path: 'Downloaded' };
    } catch (error) {
      console.error('[StorageQuota] Browser download error:', error);
      return { success: false };
    }
  }

  /**
   * Convert Blob to base64
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Remove data URL prefix if present
        const base64Data = base64.includes('base64,') ? base64.split('base64,')[1] : base64;
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Check if should use local storage (quota exceeded)
   */
  async shouldUseLocalStorage(fileSize: number): Promise<boolean> {
    const quotaCheck = await this.checkQuotaBeforeUpload(fileSize);
    return quotaCheck ? !quotaCheck.allowed : false;
  }

  /**
   * Format bytes to human-readable size
   */
  formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * Get warning level based on usage percentage
   */
  getWarningLevel(percentUsed: number): 'normal' | 'warning' | 'critical' {
    if (percentUsed >= 95) return 'critical';
    if (percentUsed >= 80) return 'warning';
    return 'normal';
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cachedStatus = null;
    this.cacheTimestamp = 0;
  }
}

export default StorageQuotaService;
